import { Language } from '@quizdb/common';
import tesseract from 'tesseract.js';
import type { Block, Worker as TesseractWorker, WorkerOptions } from 'tesseract.js';
import { MaterialPrepError, OCR_WORKERS } from '../extraction/extractionTypes';
import { keepConfidentBlocks } from './ocrText';
import { CreateOcr, OCR_ASSET_PATH, OCR_LANGUAGES, OcrBlock, OcrEngine } from './ocrTypes';

/*
 * The OCR engine: Tesseract.js, self-hosted under OCR_ASSET_PATH with no CDN (PHO-D2), run on a
 * scheduler with OCR_WORKERS workers that is terminated with the preparation (PHO-D10).
 *
 * Checked against tesseract.js 7.0.0 (`src/index.d.ts`, `src/createWorker.js`,
 * `src/createScheduler.js`):
 * - `createWorker(langs, oem, options, config)` loads the core, then the model, then initialises.
 *   A core that fails to load rejects it. A model that fails to load does NOT: the
 *   `loadLanguage`/`initialize` rejection is swallowed by its start-up chain, so the promise never
 *   settles, and the failure reaches only `options.errorHandler` (without one, it is thrown
 *   uncaught). Each worker therefore gets an `errorHandler` that, during start-up, turns the
 *   failure into a rejection (PHO-D11). After start-up it does nothing, since a failed recognize is
 *   already reported by its `addJob` rejection. A worker whose `createWorker` never settles cannot
 *   be terminated, as no handle to it is ever returned.
 * - `scheduler.terminate()` resolves without awaiting its workers' termination, and leaves any
 *   queued job unsettled, so every `read` is raced against `terminate()`.
 * - With `corePath` a directory and the LSTM-only engine, the worker picks one of the three
 *   `-lstm.wasm.js` cores by feature detection. `workerBlobURL: false` starts the worker straight
 *   from `workerPath`.
 * - The module's types use `export =`, so it is imported by default.
 * - Tesseract.js sets no page segmentation mode, so the engine falls back to its library default,
 *   a single uniform block of text. A photographed page with columns and pictures then reads as one
 *   block whose mean confidence the pictures' noise drags under OCR_MIN_BLOCK_CONFIDENCE, and the
 *   whole page is dropped. Each worker is therefore set to automatic layout analysis at start-up,
 *   so the confidence filter sees Tesseract's real blocks (PHO-D7).
 */

/** Automatic page segmentation, what the Tesseract command line uses (PHO-D7). */
const OCR_WORKER_PARAMETERS = { tessedit_pageseg_mode: tesseract.PSM.AUTO };

/** Paths must match the files the web build copies under OCR_ASSET_PATH (PHO-D2). */
const OCR_WORKER_OPTIONS = {
  workerPath: `${OCR_ASSET_PATH}/worker.min.js`,
  corePath: `${OCR_ASSET_PATH}/core`,
  langPath: `${OCR_ASSET_PATH}/lang`,
  gzip: true,
  workerBlobURL: false,
} satisfies Partial<WorkerOptions>;

type TesseractModule = Pick<typeof tesseract, 'createWorker' | 'createScheduler'>;

const noop = () => {};

const abortError = () => new DOMException('Aborted', 'AbortError');

/**
 * Maps Tesseract's blocks to `OcrBlock`s: the block's non-empty trimmed paragraphs joined by a
 * blank line, and the mean confidence of its words, or the block's own confidence when it has no
 * words (PHO-D7).
 */
export const toOcrBlocks = (blocks: Block[] | null): OcrBlock[] =>
  (blocks ?? []).map((block) => {
    const words = block.paragraphs.flatMap((paragraph) =>
      paragraph.lines.flatMap((line) => line.words)
    );
    return {
      text: block.paragraphs
        .map((paragraph) => paragraph.text.trim())
        .filter((text) => text !== '')
        .join('\n\n'),
      confidence:
        words.length === 0
          ? block.confidence
          : words.reduce((sum, word) => sum + word.confidence, 0) / words.length,
    };
  });

/**
 * Starts one worker. Resolves once it is loaded and set to automatic page segmentation, or rejects
 * when its core or model fails to load or the setting fails. `onCreated` receives the worker
 * whenever `createWorker` resolves, even after a failure.
 */
const startWorker = (
  deps: TesseractModule,
  code: string,
  onCreated: (worker: TesseractWorker) => void
): Promise<void> => {
  let starting = true;
  let failStartup: (reason: unknown) => void = noop;
  const startupFailed = new Promise<never>((_, reject) => {
    failStartup = reject;
  });
  const created = new Promise<TesseractWorker>((resolve) =>
    resolve(
      deps.createWorker(code, tesseract.OEM.LSTM_ONLY, {
        ...OCR_WORKER_OPTIONS,
        errorHandler: (error: unknown) => {
          if (starting) failStartup(error);
        },
      })
    )
  );
  created.then(onCreated, noop);
  const configured = created.then((worker) => worker.setParameters(OCR_WORKER_PARAMETERS));
  return Promise.race([configured.then(noop), startupFailed]).finally(() => {
    starting = false;
  });
};

/**
 * Loads a scheduler with OCR_WORKERS workers for `language`. Rejects with
 * `MaterialPrepError('OcrUnavailable')` when any worker fails to load, after terminating the
 * workers already made (PHO-D11).
 */
export const createTesseractOcrWith = async (
  language: Language,
  deps: { tesseract: TesseractModule } = { tesseract }
): Promise<OcrEngine> => {
  const code = OCR_LANGUAGES[language];
  const workers: TesseractWorker[] = [];
  let failed = false;
  const keep = (worker: TesseractWorker) => {
    if (failed) {
      worker.terminate().catch(noop);
    } else {
      workers.push(worker);
    }
  };

  let scheduler: ReturnType<TesseractModule['createScheduler']>;
  try {
    scheduler = deps.tesseract.createScheduler();
    await Promise.all(
      Array.from({ length: OCR_WORKERS }, () => startWorker(deps.tesseract, code, keep))
    );
  } catch {
    failed = true;
    await Promise.all(workers.map((worker) => worker.terminate().catch(noop)));
    throw new MaterialPrepError('OcrUnavailable');
  }
  workers.forEach((worker) => scheduler.addWorker(worker));

  let terminated = false;
  let termination: Promise<void> | undefined;
  let rejectTerminated: (reason: unknown) => void = noop;
  const whenTerminated = new Promise<never>((_, reject) => {
    rejectTerminated = reject;
  });
  whenTerminated.catch(noop);

  const read = async (image: Blob): Promise<string> => {
    if (terminated) throw abortError();
    const job = scheduler.addJob('recognize', image, {}, { blocks: true, text: false }).then(
      (result) => keepConfidentBlocks(toOcrBlocks(result.data.blocks)),
      () => {
        throw terminated ? abortError() : new MaterialPrepError('unknown');
      }
    );
    return Promise.race([job, whenTerminated]);
  };

  const terminate = (): Promise<void> => {
    if (!termination) {
      terminated = true;
      rejectTerminated(abortError());
      termination = new Promise<unknown>((resolve) => resolve(scheduler.terminate())).then(
        noop,
        noop
      );
    }
    return termination;
  };

  return { read, terminate };
};

/** The app's `CreateOcr`: Tesseract.js, self-hosted (PHO-D2). */
export const createTesseractOcr: CreateOcr = (language) => createTesseractOcrWith(language);
